import { format as formatFns, differenceInMilliseconds } from 'date-fns'
import { useCallback } from 'react'
import useCurrentLocale from './use-current-locale'
import { useTranslation } from 'react-i18next'

export default function useFormatDate() {
  const ONE_HOUR_MS = 1000 * 60 * 60
  const ONE_DAY_MS = ONE_HOUR_MS * 24
  const { dateFnsLocale } = useCurrentLocale()
  const { t } = useTranslation()

  /**
   * Format date function.
   *
   * Allows to specify the format you want in accordance to date-fns formats specs (https://date-fns.org/docs/format)
   *
   * @param {Date} intervalSinceDate if provided return minutes or hours (max 1 day) enlasped from this date
   *
   * EXAMPLES:
   * - date = `2023-01-15 10:20` - intervalSinceDate = `2023-01-15 10:25` => `5 minutes ago`
   * - date = `2023-01-15 10:20` - intervalSinceDate = `2023-01-15 11:21` => `1 hour ago`
   * - date = `2023-01-15 10:20` - intervalSinceDate = `2023-01-16 11:00` => *formatted date*
   */
  const formatDate = useCallback(
    (
      date: Date | number,
      format?: string,
      options?: {
        locale?: Locale
        weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6
        firstWeekContainsDate?: number
        useAdditionalWeekYearTokens?: boolean
        useAdditionalDayOfYearTokens?: boolean
      },
      intervalSinceDate?: Date,
    ): string => {
      try {
        const fnsOptions = options ? { locale: options.locale ?? dateFnsLocale, ...options } : { locale: dateFnsLocale }
        if (intervalSinceDate) {
          const difference = differenceInMilliseconds(intervalSinceDate, date)
          if (difference < ONE_HOUR_MS) return t('commons.minutes_ago', { count: Math.ceil(difference / 1000 / 60) })
          if (difference < ONE_DAY_MS) return t('commons.hours_ago', { count: Math.floor(difference / 1000 / 60 / 60) })
        }
        return formatFns(date, format || 'P', fnsOptions)
      } catch (error) {
        console.log('Invalid date, unable to format!')
        return ''
      }
    },
    [dateFnsLocale],
  )

  return formatDate
}
